import { Document } from "../Models/company";
import { AppendixItem, PageRange } from "../Models/evaluation";
import { AnnexDocument } from "../Core/useAnnexActions";
import { PredefinedSectionDocument } from "../Models/appendixStructure";

export function applyOrder<T extends { order?: number | string; subsections?: T[] }>(
  items: T[]
): T[] {
  return items.map((doc, index) => {
    const order = index + 1;
    if (doc.subsections && doc.subsections.length > 0) {
      return {
        ...doc,
        order,
        subsections: doc.subsections.map((sub, subIndex) => ({
          ...sub,
          // Use string to support two-digit subsection numbers (20.10, 20.11, ...)
          order: `${order}.${subIndex + 1}`,
        })),
      } as T;
    }
    return { ...doc, order } as T;
  });
}

export function applyCustomIds<
  T extends { order?: number | string; subsections?: T[]; name: string; id?: string }
>(items: T[]): T[] {
  return items.map((doc) => {
    if (doc.subsections && doc.subsections.length > 0) {
      return {
        ...doc,
        id: `${doc.order}-${doc.name}`,
        subsections: doc.subsections.map((sub) => ({
          id: `${sub.order}-${sub.name}`,
          ...sub,
        })),
      } as T;
    }
    return { ...doc, id: `${doc.order}-${doc.name}` } as T;
  });
}

export const filterAvailableDocuments = (
  annex?: AnnexDocument[],
  documents?: Document[]
): Document[] => {
  if (!documents) return [];

  if (!annex) return documents || [];

  const annexIds = new Set<number>();

  annex.forEach((d) => {
    if (d.id) annexIds.add(d.id);
    d.subsections?.forEach((sub) => {
      if (sub.id) annexIds.add(sub.id);
    });
  });
  return documents.filter((d) => !annexIds.has(d.id));
};

export const removeAnnexDocument = (
  annex: AnnexDocument[],
  docId: number
): AnnexDocument[] => {
  const filtered = annex.filter((doc) => doc.id !== docId);

  if (filtered.length < annex.length) {
    return filtered;
  }

  return annex.map((parent) => {
    if (!parent.subsections) return parent;

    const filteredSubs = parent.subsections.filter((sub) => sub.id !== docId);

    if (filteredSubs.length < parent.subsections.length) {
      return { ...parent, subsections: filteredSubs };
    }

    return parent;
  });
};

export const insertAnnexDocument = (
  annex: AnnexDocument[],
  document: AnnexDocument,
  insertAtId: number
): AnnexDocument[] => {
  const insertIndex = annex.findIndex((d) => d.id === insertAtId);

  if (insertIndex !== -1) {
    const newArr = [...annex];
    newArr.splice(insertIndex, 0, document);
    return newArr;
  }

  return annex.map((parent) => {
    if (!parent.subsections) return parent;

    const subInsertIndex = parent.subsections.findIndex(
      (s) => s.id === insertAtId
    );

    if (subInsertIndex !== -1) {
      const newSubs = [...parent.subsections];
      document.subsections = undefined;
      newSubs.splice(subInsertIndex, 0, document);
      return { ...parent, subsections: newSubs };
    }

    return parent;
  });
};

export const addAnnexSubsectionToParent = (
  annex: AnnexDocument[],
  parentId: number,
  document: AnnexDocument
): AnnexDocument[] => {
  return annex.map((parent) => {
    if (parent.id !== parentId) return parent;

    document.subsections = undefined;

    return {
      ...parent,
      subsections: [...(parent.subsections ?? []), document],
    };
  });
};

export const addAnnexSection = (
  annex: AnnexDocument[],
  type: "title" | "empty_section",
  options?: {
    title?: string;
    placeholder_id?: string;
    order?: number | string;
  }
): AnnexDocument[] => {
  const defaultName = type === "title" ? "Title Section" : "Empty Section";
  const sectionName = options?.title || defaultName;

  const section: AnnexDocument = {
    id: -Math.floor(Math.random() * 1000000),
    name: sectionName,
    originalName: defaultName,
    pageRanges: null,
    type: type,
    placeholder_id: options?.placeholder_id,
    order: options?.order,
  };

  return [...annex, section];
};

export const addAnnexAvailableDocument = (
  annex: AnnexDocument[],
  doc: Document
): AnnexDocument[] => {
  const annexDoc: AnnexDocument = {
    id: doc.id,
    name: doc.name,
    originalName: doc.name,
    pageRanges: null,
    number_of_pages: doc.number_of_pages,
    type: "document",
  };

  return [...annex, annexDoc];
};

export const addAnnexAvailableSubsection = (
  annex: AnnexDocument[],
  parentId: number,
  doc: Document
): AnnexDocument[] => {
  return annex.map((parent) => {
    if (parent.id !== parentId) return parent;

    const subsection: AnnexDocument = {
      id: doc.id,
      name: doc.name,
      originalName: doc.name,
      pageRanges: null,
      number_of_pages: doc.number_of_pages,
      type: "document",
    };

    return {
      ...parent,
      subsections: [...(parent.subsections ?? []), subsection],
    };
  });
};

export const updateAnnexDocument = (
  annex: AnnexDocument[],
  docId: number,
  updates: {
    name?: string;
    pageRanges?: PageRange[] | null;
    order?: number | string;
    placeholder_id?: string;
  } = {}
): AnnexDocument[] => {
  return annex.map((doc) => {
    if (doc.id === docId) {
      return { ...doc, ...updates };
    }

    if (!doc.subsections) return doc;

    const updatedSubs = doc.subsections.map((sub) =>
      sub.id === docId ? { ...sub, ...updates } : sub
    );

    if (updatedSubs.some((sub, idx) => sub !== doc.subsections![idx])) {
      return { ...doc, subsections: updatedSubs };
    }

    return doc;
  });
};

export const findAnnexDocument = <T extends { id?: number; subsections?: T[] }>(
  annex: T[],
  docId: number
): T | null => {
  const found = annex.find((doc) => doc.id === docId);
  if (found) return found;

  const foundInSubs = annex
    .flatMap((parent) => parent.subsections ?? [])
    .find((sub) => sub.id === docId);

  return foundInSubs ?? null;
};

export const findPredefinedSection = (
  predefinedSections: PredefinedSectionDocument[],
  sectionId: string
): PredefinedSectionDocument | null => {
  const found = predefinedSections.find((doc) => doc.id === sectionId);
  if (found) return found;

  const foundInSubs = predefinedSections
    .flatMap((parent) => parent.subsections ?? [])
    .find((sub) => sub.id === sectionId);

  return foundInSubs ?? null;
};

export const createAppendix = (rawAnnex: AnnexDocument[]): AppendixItem[] => {
  const flatAnnex = rawAnnex.flatMap((doc) => [
    doc,
    ...(doc.subsections || []),
  ]);

  const appendix = flatAnnex.map((doc) => {
    const document_id = doc.type === "document" ? doc.id : undefined;

    return {
      type: doc.type,
      label: doc.name,
      document_id,
      order: doc.order?.toString() || "",
      page_ranges: doc.pageRanges || [],
    };
  });

  return appendix;
};
